import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserRound } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const afterAuth = () => {
    const pending = localStorage.getItem("bh_pending_transfer");
    if (pending) {
      localStorage.removeItem("bh_pending_transfer");
      navigate(`/accept-ownership?token=${encodeURIComponent(pending)}`, { replace: true });
    } else {
      navigate("/brands", { replace: true });
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      afterAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toasts.invalid_credentials"));
    } finally {
      setBusy(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      afterAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toasts.create_failed"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0 focus-visible:border-slate-900";
  const labelCls = "text-sm font-medium text-slate-700";
  const submitCls =
    "w-full h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 shadow-none text-sm font-medium disabled:opacity-60";

  const isRegister = tab === "register";

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white antialiased"
      data-testid="login-page"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
    >
      {/* Left — form */}
      <div className="relative flex flex-col px-6 py-8 lg:px-12 lg:py-10 bg-white">
        <a
          href="https://thebrandhub.nl"
          target="_blank"
          rel="noreferrer"
          className="text-slate-900 text-base tracking-tight hover:text-slate-600 transition-colors"
          style={{ fontFamily: 'Figtree, Inter, ui-sans-serif, system-ui, sans-serif', fontWeight: 500 }}
          data-testid="brand-hub-logo"
        >
          The Brand Hub
        </a>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                <UserRound className="w-5 h-5 text-slate-500" strokeWidth={1.75} />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
                {isRegister ? "Create your account" : "Welcome back"}
              </h1>
              <p className="text-sm text-slate-500 mt-1.5 text-center">
                {isRegister ? "Sign up to start building your brand hub." : "Sign in to The Brand Hub."}
              </p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 bg-slate-100 rounded-md p-1 h-9 w-[70%] mx-auto mb-20">
                <TabsTrigger
                  value="login"
                  data-testid="tab-login"
                  className="rounded-sm text-sm font-medium text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                >
                  Sign in
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  data-testid="tab-register"
                  className="rounded-sm text-sm font-medium text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                >
                  Sign up
                </TabsTrigger>
              </TabsList>

              <Button
                type="button"
                data-testid="google-login-btn"
                onClick={handleGoogle}
                className="w-full h-10 rounded-md bg-slate-50 border border-slate-200 text-slate-900 hover:bg-slate-100 shadow-none text-sm font-medium justify-center"
              >
                <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.4-4.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-8L6.2 34C9.5 40 16.2 45.5 24 45.5z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.2 5.2C41 35.6 44.5 30.7 44.5 25c0-1.5-.2-3-.9-4.5z" />
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">Or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <TabsContent value="login" className="mt-0">
                <form onSubmit={onLogin} className="space-y-4">
                  <div>
                    <Label className={labelCls}>Email</Label>
                    <Input
                      data-testid="login-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between">
                      <Label className={labelCls}>Password</Label>
                      <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-slate-500 hover:text-slate-900">
                        Forgot password?
                      </Link>
                    </div>
                    <PasswordInput
                      testid="login-password"
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={inputCls}
                      placeholder="••••••••"
                    />
                  </div>
                  <Button data-testid="login-submit" disabled={busy} type="submit" className={submitCls}>
                    {busy ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-0">
                <form onSubmit={onRegister} className="space-y-4">
                  <div>
                    <Label className={labelCls}>Name</Label>
                    <Input
                      data-testid="register-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <Label className={labelCls}>Email</Label>
                    <Input
                      data-testid="register-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <Label className={labelCls}>Password</Label>
                    <PasswordInput
                      testid="register-password"
                      required
                      minLength={6}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={inputCls}
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <Button data-testid="register-submit" disabled={busy} type="submit" className={submitCls}>
                    {busy ? "Creating account…" : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-slate-400 mt-8 text-center leading-relaxed w-[70%] mx-auto">
              By continuing you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>

      {/* Right — visual */}
      <div className="hidden lg:block" style={{ backgroundColor: "#b8cff8" }} aria-hidden="true" />
    </div>
  );
}
